import { defineComponent, ref, useModel, watch } from "vue";
import Svg from "../../core/svg";
import { addScopedGlobalListener, OptionalVueProp, VueProp } from "../../core/vueHelpers";
import { convertTo12Hour } from "../../../skyward.base.time";
import LabeledToggleSwitch from "../../core/labeledToggleSwitch";

export const timeSelectorClasses =
{
    timeSelector: "timeSelector",
    select: "timeSelector__select",
    button: "timeSelector__button",
    svg: "timeSelector__svg",
};

export interface TimeSelectorExposed
{
    focusHourInput(): void;
    focusMinuteInput(): void;
    focusAmPmToggle(): void;
}

export default /*#__PURE__*/ defineComponent(
    {
        name: "timeSelector",
        emits:
        {
            "update:hour": (_: number) => true,
            "update:minute": (_: number) => true,
            "update:amPm": (_: "AM" | "PM") => true,
        },
        props:
        {
            hour: OptionalVueProp(Number),
            minute: OptionalVueProp(Number),
            amPm: VueProp(String),
        },
        setup(props, context)
        {
            const hourInputRef = ref<HTMLSelectElement>();
            const minuteInputRef = ref<HTMLSelectElement>();
            const toggleRef = ref<HTMLLabelElement>();
            const liveRegionRef = ref<HTMLSpanElement>();
            const hour = useModel(props, "hour");
            const minute = useModel(props, "minute");
            const amPm = useModel(props, "amPm");
            const checkedLabel = ref<boolean>(amPm.value === "PM");

            watch(amPm, (newValue) =>
            {
                if (newValue === "AM")
                {
                    checkedLabel.value = false;
                }
                else
                {
                    checkedLabel.value = true;
                }
            });

            watch(checkedLabel, (newValue) =>
            {
                if (newValue)
                {
                    amPm.value = "PM";
                }
                else
                {
                    amPm.value = "AM";
                }
            });

            context.expose(
            {
                focusHourInput() { hourInputRef.value?.focus(); },
                focusMinuteInput() { minuteInputRef.value?.focus(); },
                focusAmPmToggle() { toggleRef.value?.focus(); }
            });

            addScopedGlobalListener(document.body, "keypress", handleKeypress);
            addScopedGlobalListener(document.body, "keydown", handleSelectKeydown);

            return () => (
                <div class={timeSelectorClasses.timeSelector}>
                    <select ref={hourInputRef}
                        name="TimeHour"
                        aria-label="Select Hour"
                        value={hour.value ?? ""}
                        onChange={handleHourChange}
                        class={timeSelectorClasses.select}
                    >
                        <option aria-label="No selection" value=""></option>
                        {[...Array(12)].map((_, i) =>
                        {
                            const val = i + 1;
                            return <option value={val}>{val}</option>;
                        })}
                    </select>
                    <span aria-hidden={true}>:</span>
                    <select ref={minuteInputRef}
                        name="TimeMinute"
                        aria-label="Select Minutes"
                        value={minute.value ?? ""}
                        onChange={handleMinuteChange}
                        class={timeSelectorClasses.select}
                    >
                        <option aria-label="No selection" value=""></option>
                        {[...Array(60)].map((_, i) =>
                        {
                            const val = i;
                            const valText = val.toString().padStart(2, '0');
                            return <option value={val}>{valText}</option>;
                        })}
                    </select>
                    <LabeledToggleSwitch name="TimeToggle"
                        ref={toggleRef}
                        v-model:checked={checkedLabel.value} 
                        ariaLabel="Select AM or PM"
                        CheckedStateLabel="PM"
                        UncheckedStateLabel="AM"
                        containerClassOverride="timeSelectorToggleSwitch"
                        itemClassOverride="timeSelectorToggleSwitch__item"
                        toggleClassOverride="timeSelectorToggleSwitch__toggle"
                    />
                    <button class={timeSelectorClasses.button}
                        onClick={setCurrentTime}
                        onKeydown={handleCurrentTimeKeydown}
                        aria-label="Select Current Time">
                        <Svg name="Time"
                            showHoverEffect={false}
                            svgClasses={{
                                [timeSelectorClasses.svg]: true
                            }}
                            isButton={true} />
                    </button>
                    <span ref={liveRegionRef} class="sr-only" aria-live="polite"></span>
                </div>
            );

            function handleSelectKeydown(event: JQuery.KeyDownEvent)
            {
                const key = event.key.toUpperCase();

                if (event.target === hourInputRef.value)
                {
                    if (key === "ARROWRIGHT" || key === "ARROWLEFT")
                    {
                        event.preventDefault()
                        return;
                    }

                    if (!event.altKey)
                    {
                        if (key === "ARROWDOWN")
                        {
                            if (hour.value == undefined || isNaN(hour.value) || hour.value === 12)
                            {
                                hour.value = 1;
                            }
                            else
                            {
                                hour.value++;
                            }
                        }
                        else if (key === "ARROWUP")
                        {
                            if (hour.value == undefined || hour.value === 1)
                            {
                                hour.value = 12;
                            }
                            else
                            {
                                hour.value--;
                            }
                        }
                    }
                }
                else if (event.target === minuteInputRef.value)
                {
                    if (key === "ARROWRIGHT" || key === "ARROWLEFT")
                    {
                        event.preventDefault()
                        return;
                    }

                    if (!event.altKey)
                    {
                        if (key === "ARROWDOWN")
                        {
                            if (minute.value == undefined || isNaN(minute.value) || minute.value === 59)
                            {
                                minute.value = 0;
                            }
                            else
                            {
                                minute.value++;
                            }
                        }
                        else if (key === "ARROWUP")
                        {
                            if (minute.value == undefined || minute.value === 0)
                            {
                                minute.value = 59;
                            }
                            else
                            {
                                minute.value--;
                            }
                        }
                    }
                }
            }

            function handleKeypress(event: JQuery.KeyPressEvent)
            {
                const key = event.key.toUpperCase();

                if (key === "A")
                {
                    amPm.value = "AM";
                }
                else if (key === "P")
                {
                    amPm.value = "PM";
                }

                if (event.target === hourInputRef.value || event.target === minuteInputRef.value)
                {
                    if (key === "ENTER")
                    {
                        event.preventDefault();
                    }
                }
            }

            function handleHourChange(event: Event)
            {
                const input = event.target as HTMLSelectElement;
                const currentValue = input.value;
                hour.value = parseInt(currentValue);
            }

            function handleMinuteChange(event: Event)
            {
                const input = event.target as HTMLSelectElement;
                const currentValue = input.value;
                minute.value = parseInt(currentValue);
            }

            function setCurrentTime()
            {
                const now = new Date();
                const currentHours = now.getHours();
                let newAmPm = "AM";
                if (currentHours >= 12)
                {
                    newAmPm = "PM";
                }
                const newHour = convertTo12Hour(currentHours);
                const newMinute = now.getMinutes();

                hour.value = newHour;
                minute.value = newMinute;
                amPm.value = newAmPm;

                const ariaAnnouncement = `${newHour}:${newMinute.toString().padStart(2, "0")} ${newAmPm} Selected`

                if (liveRegionRef.value)
                {
                    if (liveRegionRef.value.innerHTML && ariaAnnouncement === liveRegionRef.value.innerHTML)
                    {
                        liveRegionRef.value.innerHTML += "&nbsp";
                    }
                    else
                    {
                        liveRegionRef.value.innerHTML = ariaAnnouncement;
                    }
                }
            }

            function handleCurrentTimeKeydown(event: KeyboardEvent)
            {
                if (event.key === "Enter")
                {
                    event.preventDefault();
                    setCurrentTime();
                }
            }
        }
    }
);